import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createStoneRequirement, listStoneSuppliers, uploadImage, getImageUrl, formatApiError } from '@/utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import CollapsibleLayout from '@/components/CollapsibleLayout';
import StoneTabs from '@/components/stones/StoneTabs';
import { STONE_KINDS, GEMSTONE_TYPES, CERTIFICATE_TYPES } from '@/utils/constants';
import { ArrowLeft, Save, Loader2, Upload, X } from 'lucide-react';

const EMPTY = {
  stone_kind: 'natural',
  stone_kind_other: '',
  gemstone_type: 'Diamond',
  size_mm: '',
  dimensions: { l: '', b: '', h: '' },
  certified: false,
  certificate_type: 'none',
  tracer: false,
  custom_cut: false,
  ct_per_stone: '',
  qty: '',
  total_ctw: '',
  sample_images: [],
  notes: '',
  assigned_supplier_ids: [],
  deadline: '',
};

export default function CreateStoneRequirementPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [suppliers, setSuppliers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    listStoneSuppliers().then(res => setSuppliers((res.data.data || []).filter(s => s.status === 'approved')))
      .catch(() => {});
  }, []);

  // Auto-compute total_ctw = qty * ct_per_stone when both present and user hasn't typed total
  useEffect(() => {
    const qty = parseFloat(form.qty), perCt = parseFloat(form.ct_per_stone);
    if (qty && perCt && !form.total_ctw) {
      setForm(f => ({ ...f, total_ctw: (qty * perCt).toFixed(3) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.qty, form.ct_per_stone]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setDim = (k, v) => setForm(f => ({ ...f, dimensions: { ...f.dimensions, [k]: v } }));
  const toggleSupplier = (id) => setForm(f => ({
    ...f,
    assigned_supplier_ids: f.assigned_supplier_ids.includes(id)
      ? f.assigned_supplier_ids.filter(x => x !== id)
      : [...f.assigned_supplier_ids, id],
  }));

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const paths = [];
      for (const f of files) {
        const res = await uploadImage(f);
        // Backend /api/upload returns { image: "/api/uploads/<filename>" } — not `path`.
        // Reading `.path` gave undefined, which then failed Pydantic's "must be string"
        // validation on sample_images.
        const url = res.data?.image || res.data?.path || res.data?.url;
        if (url) paths.push(url);
      }
      if (!paths.length) throw new Error('Upload returned no usable path');
      set('sample_images', [...form.sample_images, ...paths]);
    } catch (err) { toast.error('Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const removeImage = (path) => set('sample_images', form.sample_images.filter(p => p !== path));

  const submit = async () => {
    if (!form.gemstone_type) { toast.error('Stone type is required'); return; }
    if (!form.assigned_supplier_ids.length) {
      if (!confirm('No suppliers assigned. The requirement will be saved but no one will see it. Continue?')) return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        ct_per_stone: form.ct_per_stone ? parseFloat(form.ct_per_stone) : null,
        qty: form.qty ? parseInt(form.qty) : null,
        total_ctw: form.total_ctw ? parseFloat(form.total_ctw) : null,
        dimensions: {
          l: form.dimensions.l ? parseFloat(form.dimensions.l) : null,
          b: form.dimensions.b ? parseFloat(form.dimensions.b) : null,
          h: form.dimensions.h ? parseFloat(form.dimensions.h) : null,
        },
      };
      const res = await createStoneRequirement(payload);
      toast.success(`Created ${res.data.data.requirement_no}`);
      navigate(`/stones/${res.data.data.id}`);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to create'));
    } finally { setSaving(false); }
  };

  return (
    <CollapsibleLayout>
      <StoneTabs active="requirements" />
      <div className="max-w-[1100px] mx-auto p-4 sm:p-6 space-y-5">
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/stones')}>
              <ArrowLeft size={14} />
            </Button>
            <h1 className="text-lg font-semibold">New Stone Requirement</h1>
          </div>
          <Button onClick={submit} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-requirement-btn">
            {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
            Save & Send to Suppliers
          </Button>
        </header>

        <section className="bg-white rounded-lg border p-5 space-y-3">
          <h2 className="text-xs font-semibold text-muted-foreground">STONE</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-[10px] text-muted-foreground">Type *</Label>
              <Select value={form.stone_kind} onValueChange={v => set('stone_kind', v)}>
                <SelectTrigger className="h-8 text-xs mt-0.5" data-testid="stone-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STONE_KINDS.map(k => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.stone_kind === 'other' && (
              <div>
                <Label className="text-[10px] text-muted-foreground">Specify *</Label>
                <Input value={form.stone_kind_other} onChange={e => set('stone_kind_other', e.target.value)} className="h-8 text-xs mt-0.5" placeholder="e.g. Doublet" />
              </div>
            )}
            <div>
              <Label className="text-[10px] text-muted-foreground">Stone *</Label>
              <Select value={form.gemstone_type} onValueChange={v => set('gemstone_type', v)}>
                <SelectTrigger className="h-8 text-xs mt-0.5" data-testid="gemstone-type"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-[320px]">
                  {GEMSTONE_TYPES.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Size (mm)</Label>
              <Input value={form.size_mm} onChange={e => set('size_mm', e.target.value)} className="h-8 text-xs mt-0.5" placeholder="e.g. 3.5mm or 3x5mm" data-testid="size-mm" />
            </div>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            <div>
              <Label className="text-[10px] text-muted-foreground">L</Label>
              <Input type="number" step="0.01" value={form.dimensions.l} onChange={e => setDim('l', e.target.value)} className="h-8 text-xs mt-0.5" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">B</Label>
              <Input type="number" step="0.01" value={form.dimensions.b} onChange={e => setDim('b', e.target.value)} className="h-8 text-xs mt-0.5" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">H</Label>
              <Input type="number" step="0.01" value={form.dimensions.h} onChange={e => setDim('h', e.target.value)} className="h-8 text-xs mt-0.5" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Ct / stone</Label>
              <Input type="number" step="0.001" value={form.ct_per_stone} onChange={e => set('ct_per_stone', e.target.value)} className="h-8 text-xs mt-0.5" data-testid="ct-per-stone" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Qty</Label>
              <Input type="number" value={form.qty} onChange={e => set('qty', e.target.value)} className="h-8 text-xs mt-0.5" data-testid="qty" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Total Ct</Label>
              <Input type="number" step="0.001" value={form.total_ctw} onChange={e => set('total_ctw', e.target.value)} className="h-8 text-xs mt-0.5" data-testid="total-ctw" />
            </div>
          </div>
          <div className="flex flex-wrap gap-3 sm:gap-4">
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <Checkbox checked={form.certified} onCheckedChange={v => set('certified', !!v)} data-testid="certified" /> Certified required
            </label>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <Checkbox checked={form.tracer} onCheckedChange={v => set('tracer', !!v)} data-testid="tracer" /> Tracer required
            </label>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <Checkbox checked={form.custom_cut} onCheckedChange={v => set('custom_cut', !!v)} data-testid="custom-cut" /> Custom cut
            </label>
          </div>
          {form.certified && (
            <div className="mt-2 max-w-xs">
              <Label className="text-[10px] text-muted-foreground">Certificate type required</Label>
              <Select value={form.certificate_type || 'none'} onValueChange={v => set('certificate_type', v)}>
                <SelectTrigger className="h-8 text-xs mt-0.5" data-testid="certificate-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CERTIFICATE_TYPES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </section>

        <section className="bg-white rounded-lg border p-5 space-y-3">
          <h2 className="text-xs font-semibold text-muted-foreground">SAMPLE IMAGES (from client)</h2>
          <div className="flex gap-3 flex-wrap items-start">
            {form.sample_images.map((p, i) => (
              <div key={i} className="relative">
                <img src={getImageUrl(p)} alt="" className="w-24 h-24 object-cover rounded border" />
                <button type="button" onClick={() => removeImage(p)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                  <X size={10} />
                </button>
              </div>
            ))}
            <label className="w-24 h-24 border-2 border-dashed rounded flex flex-col items-center justify-center cursor-pointer hover:bg-slate-50 text-xs text-muted-foreground" title="Click to add — you can select multiple images at once">
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={16} />}
              <span className="mt-1 text-center leading-tight">Add<br/><span className="text-[9px]">(multiple OK)</span></span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} disabled={uploading} data-testid="upload-images" />
            </label>
          </div>
        </section>

        <section className="bg-white rounded-lg border p-5 space-y-3">
          <h2 className="text-xs font-semibold text-muted-foreground">NOTES</h2>
          <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
            className="w-full text-xs p-2 border rounded-md bg-slate-50 min-h-[80px] resize-y"
            placeholder="Any other details suppliers should know about this requirement…"
            data-testid="notes" />
          <div>
            <Label className="text-[10px] text-muted-foreground">Deadline (optional)</Label>
            <Input type="date" value={form.deadline} onChange={e => set('deadline', e.target.value)} className="h-8 text-xs mt-0.5 w-fit" />
          </div>
        </section>

        <section className="bg-white rounded-lg border p-5 space-y-3">
          <h2 className="text-xs font-semibold text-muted-foreground">INVITE SUPPLIERS *</h2>
          {suppliers.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No suppliers yet. <button className="text-teal-600 underline" onClick={() => navigate('/stones/suppliers')}>Add suppliers</button> first.
            </p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {suppliers.map(s => {
                const checked = form.assigned_supplier_ids.includes(s.id);
                return (
                  <label key={s.id} className={`flex items-center gap-2 text-xs p-2 border rounded cursor-pointer ${checked ? 'bg-teal-50 border-teal-300' : 'hover:bg-slate-50'}`}>
                    <Checkbox checked={checked} onCheckedChange={() => toggleSupplier(s.id)} data-testid={`pick-${s.id}`} />
                    <div className="min-w-0">
                      <div className="font-medium truncate">{s.name}</div>
                      <div className="text-muted-foreground truncate text-[10px]">{s.company_name || s.email}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </CollapsibleLayout>
  );
}
